import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { authApi } from "./api";
import type { UserProfile } from "./types";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { globalQueryClient } from "@shared/api/queries/queryClients";
import {
  logEvent,
  setUserAndProps,
  resetPosthog,
} from "@shared/analytics/analytics";
import { coreApi } from "@features/core/api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface User {
  id: string;
  email: string;
  token: string;
}

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  userProfile: UserProfile | undefined;
  isProfileLoading: boolean;
  profileError: Error | null;
}

const CACHED_PROFILE_KEY = "cached_user_profile";
const CACHED_USER_PERMISSION_KEY = "cached_user_permissions";

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const utilityId = useRemoteUtilityId();

  // Check for existing auth token on mount
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      if (!user) {
        setUser({ id: "", email: "", token });
      }
    }
  }, [user]);

  // React Query hook for user profile
  const {
    data: userProfile,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      // First check if we have cached data in React Query
      const cachedData = queryClient.getQueryData(["userProfile"]);
      if (cachedData) {
        return cachedData as UserProfile;
      }

      // Then check localStorage
      const storedProfile = localStorage.getItem(CACHED_PROFILE_KEY);
      if (storedProfile) {
        const parsedProfile = JSON.parse(storedProfile) as UserProfile;
        // Update React Query cache with localStorage data
        queryClient.setQueryData(["userProfile"], parsedProfile);
        return parsedProfile;
      }

      // If no cached data, fetch from API
      const response = await authApi.getUserProfile();
      setUserAndProps(String(response?.user?.id), {
        name: response?.user?.name,
        role: response?.roleCode,
        email: response?.user?.email,
        username: response?.user?.username,
      });
      logEvent("Login Success", {
        email: response?.user?.email,
        timestamp: new Date().toISOString(),
      });
      // Store in localStorage as backup
      localStorage.setItem(CACHED_PROFILE_KEY, JSON.stringify(response));
      return response;
    },
    enabled: !!user, // Only fetch when user is logged in
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Check and fetch profile on mount if needed
  useEffect(() => {
    const checkAndFetchProfile = async () => {
      const token = localStorage.getItem("auth_token");
      if (token && !userProfile && !isProfileLoading) {
        try {
          await refetchProfile();
        } catch (err) {
          console.error("Failed to fetch user profile:", err);
          // If profile fetch fails, clear auth state
          localStorage.removeItem("auth_token");
          localStorage.removeItem(CACHED_PROFILE_KEY);
          setUser(null);
          queryClient.clear();
        }
      }
    };

    checkAndFetchProfile();
  }, [userProfile, isProfileLoading, refetchProfile, queryClient]);


  const login = useCallback(
    async (email: string, password: string) => {
      try {
        setLoading(true);
        setError(null);

        logEvent("Login Attempted", {
          email,
          timestamp: new Date().toISOString(),
        });

        // 1. Login API
        const response = await authApi.login({ email, password });
        localStorage.setItem("auth_token", response.user.token);
        setUser(response.user);

        // 2. Get user profile
        const profile = await authApi.getUserProfile();
        const utilityID = String(profile?.utility?.id);
 

        // Save profile to query cache + localStorage
        const profileKey = QueryKeyFactory.global.user.profile();
        globalQueryClient.setQueryData(profileKey, profile);
        localStorage.setItem(CACHED_PROFILE_KEY, JSON.stringify(profile));

   
        // if (profile) {
        try {
          const permissions = await coreApi.getUserRolePermissionAccess(utilityID);

          const permissionKey =
            QueryKeyFactory.module.onboarding.userRolePermission();
          globalQueryClient.setQueryData(permissionKey, permissions);

          localStorage.setItem(
            CACHED_USER_PERMISSION_KEY,
            JSON.stringify(permissions)
          );

          
        } catch (permErr) {
          console.error("Failed to fetch user permissions", permErr);
          logEvent("Permission Fetch Failed", { email });
        }
        // }

        // 4. Navigate to landing page (from profile)

        if (profile.landingPage) {
          const target = profile.landingPage.startsWith("/")
            ? profile.landingPage
            : `/${profile.landingPage}`;
          navigate(target);
        } else {
          navigate("/fallback-dashboard");
        }
      }
       catch (err) {
        logEvent("Login Failed", {
          email,
          timestamp: new Date().toISOString(),
        });
        const errorMessage =
          err instanceof Error ? err.message : "An error occurred during login";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
      logEvent("Log Out Success");
      resetPosthog();
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      localStorage.clear();
      // Clear React Query cache on logout
      queryClient.clear();
      navigate("/login");
    }
  }, [navigate, queryClient]);

  const forgotPassword = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      await authApi.forgotPassword(email);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred during password reset";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      await authApi.resetPassword(token, password);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred during password reset";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    loading,
    error,
    login,
    logout,
    forgotPassword,
    resetPassword,
    userProfile,
    isProfileLoading,
    profileError,
  };
};
